import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import DictationInput from '../components/DictationInput';
import { Users, Plus, Calendar, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate } from '../lib/utils';

const GROUP_TYPES = [
  { value: 'mrt', label: 'MRT (Moral Reconation Therapy)' },
  { value: 'heat', label: 'H.E.A.T. For Men' },
  { value: 'heat_for_her', label: 'H.E.A.T. For Her' },
  { value: 'heat_for_youth', label: 'H.E.A.T. For Youth' },
  { value: 'dbt', label: 'DBT (Dialectical Behavior Therapy)' },
  { value: 'parenting', label: 'Parenting' },
  { value: 'core8', label: 'Core 8 (Criminal Thinking Errors)' },
  { value: 'other', label: 'Other' },
];

export default function GroupNotesPage() {
  const { user, axiosAuth } = useAuth();
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    group_topic: '',
    group_type: '',
    date: new Date().toISOString().split('T')[0],
    participant_engagement: '',
    group_outcomes: '',
    next_group_plan: '',
    additional_notes: ''
  });
  const [creating, setCreating] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);
  const api = axiosAuth();

  const load = () => api.get('/group-notes').then(r => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.group_topic) return toast.error('Group topic is required');
    if (!form.group_type) return toast.error('Please select a group type');
    setCreating(true);
    try {
      await api.post('/group-notes', form);
      toast.success('Group note saved!');
      setOpen(false);
      setForm({ group_topic: '', group_type: '', date: new Date().toISOString().split('T')[0], participant_engagement: '', group_outcomes: '', next_group_plan: '', additional_notes: '' });
      load();
    } catch { toast.error('Failed to save'); } finally { setCreating(false); }
  };

  const updateField = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const groupLabel = (val) => GROUP_TYPES.find(g => g.value === val)?.label || val;

  return (
    <div className="space-y-6" data-testid="group-notes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Group Notes</h1>
          <p className="text-muted-foreground text-sm mt-1">Document group sessions and outcomes</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-group-note-btn"><Plus size={16} /> New Group Note</Button></DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>New Group Session Note</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Group Type</Label>
                <Select value={form.group_type} onValueChange={v => updateField('group_type', v)}>
                  <SelectTrigger data-testid="group-type-select"><SelectValue placeholder="Select group type..." /></SelectTrigger>
                  <SelectContent>
                    {GROUP_TYPES.map(g => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Group Topic</Label>
                  <Input value={form.group_topic} onChange={e => updateField('group_topic', e.target.value)} placeholder="e.g., Relapse Prevention" data-testid="group-topic" />
                </div>
                <div className="space-y-1.5"><Label className="text-sm">Date</Label><Input type="date" value={form.date} onChange={e => updateField('date', e.target.value)} /></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Participant Engagement Summary</Label>
                <DictationInput
                  value={form.participant_engagement}
                  onChange={(val) => updateField('participant_engagement', val)}
                  placeholder="How did participants engage? Who was active? Any concerns?"
                  rows={2}
                  data-testid="group-notes-participant_engagement"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Group Outcomes</Label>
                <DictationInput
                  value={form.group_outcomes}
                  onChange={(val) => updateField('group_outcomes', val)}
                  placeholder="What was accomplished? Key takeaways? Breakthroughs?"
                  rows={2}
                  data-testid="group-notes-group_outcomes"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Next Group Plan</Label>
                <DictationInput
                  value={form.next_group_plan}
                  onChange={(val) => updateField('next_group_plan', val)}
                  placeholder="What topics will be covered next? Any follow-ups?"
                  rows={2}
                  data-testid="group-notes-next_group_plan"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Additional Notes</Label>
                <DictationInput
                  value={form.additional_notes}
                  onChange={(val) => updateField('additional_notes', val)}
                  placeholder="Anything else to note about this session"
                  rows={2}
                  data-testid="group-notes-additional_notes"
                />
              </div>
              <Button onClick={handleCreate} disabled={creating} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="save-group-note">{creating ? 'Saving...' : 'Save Group Note'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {notes.length > 0 ? notes.map(n => (
          <Card key={n.id} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpandedNote(expandedNote === n.id ? null : n.id)} data-testid={`group-note-${n.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center"><Users className="w-5 h-5 text-violet-600" /></div>
                  <div>
                    <p className="font-medium">{n.group_topic}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {n.group_type && <Badge className="bg-violet-50 text-violet-700 border-0 text-[10px]">{groupLabel(n.group_type)}</Badge>}
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={10} /> {n.date} &middot; By {n.author_name}</p>
                    </div>
                  </div>
                </div>
                {expandedNote === n.id ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
              </div>
              {expandedNote === n.id && (
                <div className="space-y-2 animate-in fade-in-0 duration-200">
                  {n.participant_engagement && <div className="bg-sky-50 rounded-lg p-3 border border-sky-100"><p className="text-xs font-semibold text-sky-600 mb-1">Engagement</p><p className="text-sm">{n.participant_engagement}</p></div>}
                  {n.group_outcomes && <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100"><p className="text-xs font-semibold text-emerald-600 mb-1">Outcomes</p><p className="text-sm">{n.group_outcomes}</p></div>}
                  {n.next_group_plan && <div className="bg-amber-50 rounded-lg p-3 border border-amber-100"><p className="text-xs font-semibold text-amber-600 mb-1">Next Plan</p><p className="text-sm">{n.next_group_plan}</p></div>}
                  {n.additional_notes && <div className="bg-muted/50 rounded-lg p-3"><p className="text-xs font-semibold text-muted-foreground mb-1">Notes</p><p className="text-sm">{n.additional_notes}</p></div>}
                </div>
              )}
              {expandedNote !== n.id && (
                <p className="text-xs text-muted-foreground line-clamp-1">{n.participant_engagement || n.group_outcomes || 'No details recorded'}</p>
              )}
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center">
            <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No group notes yet.</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
